import { Component, DestroyRef, OnInit, Input, Output, SimpleChanges, OnChanges, EventEmitter, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SearchDates } from '../../models/searchDates';
import { PassDataService } from '../../services/pass-data/pass-data.service';
import { ApiService } from '../../services/api.service';
import { SafeHtml } from '@angular/platform-browser';
import { first } from 'rxjs';
import { DatesRange } from '../../models/datesRange.model';
import { PlatformService } from '../../services/platform.service';
import { BackButtonService, BackPriority } from '../../services/back-button.service';

@Component({
    selector: 'urban-searchbar',
    templateUrl: './searchbar.component.html',
    styleUrls: ['./searchbar.component.scss'],
    standalone: false
})
export class SearchbarComponent implements OnInit, OnChanges {
  @Input() public settingKey: string;
  @Input() private datesRange: DatesRange;
  @Input() public buttonToggleSelected: number = null;
  @Input() public clearDateUnsubscribeEvent: boolean;
  @Input() public clearDateEvent: boolean;
  @Input() public setLatestDates: boolean;
  @Input() public loadingEvents: boolean = false;
  @Input() public overlayBG: boolean = false;
  // Below the mobile breakpoint: a one-line summary that opens the filters in a bottom sheet.
  @Input() public compactOnMobile: boolean = true;
  @Output() private newSearch = new EventEmitter<any>();
  @Output() private stopPreviousSearch = new EventEmitter<any>();

  private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private readonly passDataService: PassDataService = inject(PassDataService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly backButton: BackButtonService = inject(BackButtonService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  @ViewChild('searchSheet') private searchSheet: TemplateRef<unknown>;
  private sheetRef: MatBottomSheetRef<unknown> | null = null;

  private static readonly maxDaysRangeSettingKey: string = 'search_max_days_range';

  // Used until the configured limit arrives, and whenever the client setting
  // is missing or malformed.
  private static readonly fallbackMaxDays: Record<string, number> = {
    'default': 3,
    'weatherstation': 6,
    'people-counter': 6,
    'platform-statistics': 10,
    'agrivoltaic': 30,
    'charger': 30,
    'parking': 30,
    'intrusion': 30,
  };

  private maxDaysRange: number;

  public startDate: number;
  public endDate: number;
  public minEndDate: Date;
  public maxEndDate: Date;
  public minStartDate: Date;
  public maxStartDate: Date;
  private previousButtonToggle: number;
  public filterPeriods: number[] = [2,24,48,72];
  public last24hSearch: boolean;
  public defaultHoursRange: number;
  public formDate: UntypedFormGroup = this.formBuilder.group({
    startDate: [{ value: null, disabled: false }],
    endDate: [{ value: null, disabled: false }]
  });
  public icons: Record<string, SafeHtml> = {};

  public get isMobile(): boolean {
    return this.platformService.isMobileViewport();
  }

  public openSheet(): void {
    if (this.sheetRef) {
      return;
    }

    this.sheetRef = this.bottomSheet.open(this.searchSheet, {
      panelClass: 'uvSheetPanel',
      autoFocus: false
    });
    const releaseBackButton: () => void = this.backButton.register(BackPriority.Sheet, () => {
      this.closeSheet();
      return true;
    });
    // Destroyed with the sheet still open, afterDismissed is never seen here.
    this.destroyRef.onDestroy(releaseBackButton);
    this.sheetRef.afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sheetRef = null;
        releaseBackButton();
      });
  }

  public closeSheet(): void {
    this.sheetRef?.dismiss();
  }

  ngOnInit(): void {
    this.maxDaysRange = SearchbarComponent.fallbackMaxDays[this.settingKey]
      ?? SearchbarComponent.fallbackMaxDays['default'];

    this.passDataService.sanIcons$.pipe(first()).subscribe(response => {
      this.icons = response;
    });

    if (this.buttonToggleSelected === null) {
      this.previousButtonToggle = null;
      this.defaultHoursRange = this.maxDaysRange * 24;
      this.initDates(this.defaultHoursRange);
    } else {
      this.defaultHoursRange = this.buttonToggleSelected;
    }

    this.resetLimitDates();

    if (this.settingKey) {
      this.applyConfiguredMaxDaysRange();
    }
  }

  private applyConfiguredMaxDaysRange(): void {
    this.apiService.getClientSettings().pipe(first()).subscribe(settings => {
      const configuredDays: number = this.readConfiguredMaxDays(settings);

      if (!configuredDays || configuredDays === this.maxDaysRange) {
        return;
      }

      this.maxDaysRange = configuredDays;

      if (this.buttonToggleSelected === null && !this.formDate.dirty) {
        this.defaultHoursRange = this.maxDaysRange * 24;
        this.initDates(this.defaultHoursRange);
      }
    });
  }

  private readConfiguredMaxDays(settings: Record<string, string>): number {
    const rawSetting: string = settings?.[SearchbarComponent.maxDaysRangeSettingKey];

    if (!rawSetting) {
      return null;
    }

    try {
      const limits: Record<string, unknown> = JSON.parse(rawSetting);
      const value: number = Number(limits?.[this.settingKey]);

      return Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
    } catch {
      return null;
    }
  }

  public onSearch(): void {
    if (this.formDate.controls.startDate.value && this.formDate.controls.endDate.value) {
      this.startDate = this.reformatStart(this.formDate.controls.startDate.value.toString());
      this.endDate = this.clampToMaxRange(
        this.startDate,
        this.reformatEnd(this.formDate.controls.endDate.value.toString())
      );

      if (this.buttonToggleSelected === 24) {
        this.last24hSearch = true;
      } else {
        this.last24hSearch = false;
      }
      
      let selectedDates: SearchDates = { startDate: this.startDate, endDate: this.endDate, last24hSearch: this.last24hSearch };
      this.newSearch.emit(selectedDates);
      this.closeSheet();
    } else {
      this.buttonToggleSelected = 24;
    }
  }

  // The inclusive one-second padding pushes a full-width selection past the limit.
  private clampToMaxRange(start: number, end: number): number {
    const maxSeconds: number = this.maxDaysRange * 24 * 60 * 60;

    return end - start > maxSeconds ? start + maxSeconds : end;
  }

  private initDates(hoursRange: number): void {
    this.formDate.reset();
    let now = new Date();
    now.setHours(now.getHours() - hoursRange);
    this.formDate.patchValue({
      startDate: new Date(now)
    });
    this.onStartDateUpdate(now);
  }

  public setTimeRange(event: number): void {
    if (event !== undefined) {
      if (event !== this.previousButtonToggle) {
        this.initDates(event);

        if(this.previousButtonToggle !== undefined) {
          this.onSearch();
        }

        this.previousButtonToggle = event;
      }
    }
  }

  // The pickers bound the range only relative to the other date: any window
  // of maxDaysRange days in the past is selectable. Picking a date that makes
  // the range invalid re-anchors the other one instead of blocking.
  public onStartDateUpdate(datePassed: Date): void {
    const start: Date = new Date(datePassed);
    const endValue: any = this.formDate.controls.endDate.value;
    const end: Date = endValue ? new Date(endValue) : null;

    if (!end || end.getTime() < start.getTime() || this.exceedsRange(start, end)) {
      this.formDate.patchValue({ endDate: this.maxEndFor(start) });
    }

    this.refreshPickerLimits();
  }

  public onEndDateUpdate(datePassed: Date): void {
    const end: Date = new Date(datePassed);
    const startValue: any = this.formDate.controls.startDate.value;
    const start: Date = startValue ? new Date(startValue) : null;

    if (!start || start.getTime() > end.getTime() || this.exceedsRange(start, end)) {
      const newStart: Date = new Date(end);
      newStart.setDate(newStart.getDate() - this.maxDaysRange);
      this.formDate.patchValue({ startDate: newStart });
    }

    this.refreshPickerLimits();
  }

  private exceedsRange(start: Date, end: Date): boolean {
    const limit: Date = new Date(start);
    limit.setDate(limit.getDate() + this.maxDaysRange);
    return end.getTime() > limit.getTime();
  }

  // Latest end allowed for a given start: start + maxDaysRange, never in the future.
  private maxEndFor(start: Date): Date {
    const now: Date = new Date();
    const candidate: Date = new Date(start);
    candidate.setDate(candidate.getDate() + this.maxDaysRange);
    return candidate.getTime() > now.getTime() ? now : candidate;
  }

  private refreshPickerLimits(): void {
    const now: Date = new Date();
    const startValue: any = this.formDate.controls.startDate.value;
    const start: Date = startValue ? new Date(startValue) : null;

    this.minStartDate = null;
    this.maxStartDate = new Date(now);
    this.minEndDate = start ? new Date(start) : null;
    this.maxEndDate = start ? this.maxEndFor(start) : new Date(now);
  }

  public removeRangeBtnActive() {
    this.previousButtonToggle = null;
    this.buttonToggleSelected = null;
  }

  public reformatStart(dateToFormat: string): number {
    let currentDate = new Date(dateToFormat);
    return Math.floor(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds()).getTime() / 1000 - 1);
  }

  public reformatEnd(dateToFormat: string): number {
    let currentDate = new Date(dateToFormat);
    return Math.ceil(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds()).getTime() / 1000 + 1);
  }

  public clearDateAndUnsubscribe(): void {
    this.stopPreviousSearch.emit();
    this.formDate.reset();
    this.removeRangeBtnActive();
  }

  public clearDate(): void {
    this.formDate.reset();
    this.removeRangeBtnActive();
  }

  public onBlankDatesFocus(): void {
    if (this.formDate.controls.startDate.value == null && this.formDate.controls.endDate.value == null) {
      this.resetLimitDates();
    }
    if (this.filterPeriods.includes(this.buttonToggleSelected)) {
      this.resetLimitDates();
      this.clearDateAndUnsubscribe();
    }
  }

  public resetLimitDates(): void {
    this.refreshPickerLimits();
  }

  private calcLatestEventDates(): void {
    let start: Date;
    if (this.datesRange.Start) {
      start = new Date(this.datesRange.Start);
    }
    else {
      start = new Date(this.datesRange.End);
      start.setHours(start.getHours() - this.defaultHoursRange);
    }
    let end: Date = new Date(this.datesRange.End);

    this.formDate.patchValue({
      startDate: start,
      endDate: end
    });

    this.refreshPickerLimits();
  }

  public setLatestEventDates(): void {
    this.calcLatestEventDates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latestDates']) {
      const latestDates = changes['latestDates'];
      if (latestDates.currentValue !== latestDates.previousValue && latestDates.firstChange == false) {
        this.calcLatestEventDates();
      }
    }

    if (changes['setLatestDates']) {
      const setLatestDates = changes['setLatestDates'];
      if (setLatestDates.currentValue !== setLatestDates.previousValue && setLatestDates.firstChange == false) {
        this.setLatestEventDates();
      }
    }

    if (changes['clearDateUnsubscribeEvent']) {
      const clearDateUnsubscribeEvent = changes['clearDateUnsubscribeEvent'];
      if (clearDateUnsubscribeEvent.currentValue !== clearDateUnsubscribeEvent.previousValue && clearDateUnsubscribeEvent.firstChange == false) {
        this.clearDateAndUnsubscribe();
      }
    }

    if (changes['clearDateEvent']) {
      const clearDateEvent = changes['clearDateEvent'];
      if (clearDateEvent.currentValue !== clearDateEvent.previousValue && clearDateEvent.firstChange == false) {
        this.clearDate();
      }
    }
  }
}
